import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  ChevronLeft, MapPin, Home, Building2, Star, Clock, 
  Plus, Edit2, Trash2, Check, X, Loader2
} from 'lucide-react';
import AddressAutocompleteInput from '@/components/AddressAutocompleteInput';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AddressManagementPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddNew, setShowAddNew] = useState(false);
  const [newAddressData, setNewAddressData] = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const [savingNew, setSavingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchAddresses();
  }, [user, navigate]);

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API_URL}/customer/addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(response.data);
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddressSelected = (address) => {
    setNewAddressData(address);
  };

  const handleSaveNewAddress = async () => {
    if (!newAddressData || !newAddressData.lat) {
      toast.error('Please select a valid address from the suggestions');
      return;
    }

    setSavingNew(true);
    try {
      await axios.post(
        `${API_URL}/customer/addresses`,
        {
          label: newLabel || null,
          formatted_address: newAddressData.formattedAddress,
          place_id: newAddressData.placeId || null,
          lat: newAddressData.lat,
          lng: newAddressData.lng,
          postcode: newAddressData.postcode || '',
          city: newAddressData.city || '',
          country: newAddressData.countryName || 'Unknown',
          country_code: newAddressData.countryCode || '',
          provider: newAddressData.provider || 'google',
          is_saved: true  // Mark as explicitly saved
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Address saved');
      setShowAddNew(false);
      setNewAddressData(null);
      setNewLabel('');
      fetchAddresses();
    } catch (error) {
      toast.error('Failed to save address');
    } finally {
      setSavingNew(false);
    }
  };

  const handleUpdateLabel = async (addressId) => {
    try {
      await axios.patch(
        `${API_URL}/customer/addresses/${addressId}`,
        { label: editLabel },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Address renamed');
      setEditingId(null);
      fetchAddresses();
    } catch (error) {
      toast.error('Failed to update address');
    }
  };

  const handleToggleSaved = async (address) => {
    try {
      await axios.patch(
        `${API_URL}/customer/addresses/${address.id}`,
        { is_saved: !address.is_saved },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(address.is_saved ? 'Address unsaved' : 'Address saved');
      fetchAddresses();
    } catch (error) {
      toast.error('Failed to update address');
    }
  };

  const handleDelete = async (addressId) => {
    if (!confirm('Delete this address?')) return;

    try {
      await axios.delete(`${API_URL}/customer/addresses/${addressId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Address deleted');
      fetchAddresses();
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const getIcon = (label) => {
    const labelLower = (label || '').toLowerCase();
    if (labelLower === 'home') return <Home className="w-5 h-5" />;
    if (labelLower === 'work' || labelLower === 'office') return <Building2 className="w-5 h-5" />;
    return <MapPin className="w-5 h-5" />;
  };

  const savedAddresses = addresses.filter(a => a.is_saved);
  const recentAddresses = addresses.filter(a => !a.is_saved);

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="text-neutral-600 hover:text-neutral-900">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>My Addresses</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Add New Address */}
        {!showAddNew ? (
          <Button
            onClick={() => setShowAddNew(true)}
            className="w-full mb-6 h-14 bg-black hover:bg-neutral-800 text-white rounded-xl"
            data-testid="add-address-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add New Address
          </Button>
        ) : (
          <div className="bg-white rounded-2xl border border-neutral-200 p-6 mb-6">
            <h3 className="font-semibold mb-4">Add New Address</h3>
            
            <div className="space-y-4">
              <AddressAutocompleteInput
                onAddressSelected={handleAddressSelected}
                placeholder="Search for your address (worldwide)"
                countryRestriction={null}
                showRecentAddresses={false}
                showSavedAddresses={false}
                testId="new-address-autocomplete"
              />

              <div>
                <label className="text-sm font-medium text-neutral-700 mb-1 block">
                  Label (optional)
                </label>
                <Input
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="e.g. Home, Work, Mum's House"
                  data-testid="new-address-label"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddNew(false);
                    setNewAddressData(null);
                    setNewLabel('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveNewAddress}
                  disabled={savingNew || !newAddressData?.lat}
                  className="flex-1 bg-black hover:bg-neutral-800 text-white"
                  data-testid="save-new-address-btn"
                >
                  {savingNew ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save Address
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-neutral-400" />
            <p className="text-neutral-500 mt-2">Loading addresses...</p>
          </div>
        ) : (
          <>
            {/* Saved Addresses */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Saved Addresses
              </h2>
              
              {savedAddresses.length === 0 ? (
                <div className="bg-white rounded-xl border border-neutral-200 p-6 text-center">
                  <MapPin className="w-10 h-10 mx-auto text-neutral-300 mb-2" />
                  <p className="text-neutral-500">No saved addresses yet</p>
                  <p className="text-sm text-neutral-400">Add addresses you use frequently</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {savedAddresses.map(address => (
                    <div
                      key={address.id}
                      className="bg-white rounded-xl border border-neutral-200 p-4"
                      data-testid={`saved-address-${address.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
                          {getIcon(address.label)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          {editingId === address.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={editLabel}
                                onChange={(e) => setEditLabel(e.target.value)}
                                placeholder="Enter label"
                                className="h-8 text-sm"
                                autoFocus
                              />
                              <button
                                onClick={() => handleUpdateLabel(address.id)}
                                className="p-1 hover:bg-neutral-100 rounded"
                              >
                                <Check className="w-4 h-4 text-green-600" />
                              </button>
                              <button
                                onClick={() => setEditingId(null)}
                                className="p-1 hover:bg-neutral-100 rounded"
                              >
                                <X className="w-4 h-4 text-neutral-400" />
                              </button>
                            </div>
                          ) : (
                            <div className="font-medium">
                              {address.label || 'Saved Address'}
                            </div>
                          )}
                          <div className="text-sm text-neutral-500 truncate">
                            {address.formatted_address}
                          </div>
                          <div className="text-xs text-neutral-400 mt-1">
                            {address.city}, {address.postcode}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setEditingId(address.id);
                              setEditLabel(address.label || '');
                            }}
                            className="p-2 hover:bg-neutral-100 rounded-lg"
                            title="Rename"
                          >
                            <Edit2 className="w-4 h-4 text-neutral-400" />
                          </button>
                          <button
                            onClick={() => handleToggleSaved(address)}
                            className="p-2 hover:bg-neutral-100 rounded-lg"
                            title="Unsave"
                          >
                            <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                          </button>
                          <button
                            onClick={() => handleDelete(address.id)}
                            className="p-2 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Addresses */}
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-neutral-400" />
                Recent Addresses
              </h2>
              
              {recentAddresses.length === 0 ? (
                <div className="bg-white rounded-xl border border-neutral-200 p-6 text-center">
                  <Clock className="w-10 h-10 mx-auto text-neutral-300 mb-2" />
                  <p className="text-neutral-500">No recent addresses</p>
                  <p className="text-sm text-neutral-400">Addresses from your quotes and jobs will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentAddresses.slice(0, 10).map(address => (
                    <div
                      key={address.id}
                      className="bg-white rounded-xl border border-neutral-200 p-4"
                      data-testid={`recent-address-${address.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
                          <Clock className="w-5 h-5 text-neutral-400" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            {address.formatted_address?.split(',')[0] || 'Recent Address'}
                          </div>
                          <div className="text-sm text-neutral-500">
                            {address.city}, {address.postcode}
                          </div>
                          <div className="text-xs text-neutral-400 mt-1">
                            Last used: {new Date(address.last_used_at).toLocaleDateString()}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleToggleSaved(address)}
                            className="p-2 hover:bg-neutral-100 rounded-lg"
                            title="Save this address"
                          >
                            <Star className="w-4 h-4 text-neutral-400" />
                          </button>
                          <button
                            onClick={() => handleDelete(address.id)}
                            className="p-2 hover:bg-red-50 rounded-lg"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {recentAddresses.length > 10 && (
                <p className="text-sm text-neutral-400 text-center mt-4">
                  Showing last 10 of {recentAddresses.length} recent addresses
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddressManagementPage;
